import { fmtTL } from "@/components/StatusBadge";
import { QRCodeSVG } from "qrcode.react";
import { verifyUrl, surveyUrl, logoUrl, docImageUrl, docStampUrl, docStampPos, activeDocTheme, activeDocTemplate, docBrandName, docShowBrandText, docLegalName, docAddress, docTaxLine, docPhone, docEmail, docWeb, docWebRaw, docSlogan, docBankAccounts, docFontOverride, docCfg } from "@/lib/company";

// FAST / TR Karekod (BKM-TCMB, EMVCo tabanlı TLV) — banka uygulamasıyla okutup tek dokunuşta havale/tahsilat.
const _asciiTr = (s) => String(s || "")
  .replace(/ç/g, "c").replace(/Ç/g, "C").replace(/ğ/g, "g").replace(/Ğ/g, "G")
  .replace(/ı/g, "i").replace(/İ/g, "I").replace(/ö/g, "o").replace(/Ö/g, "O")
  .replace(/ş/g, "s").replace(/Ş/g, "S").replace(/ü/g, "u").replace(/Ü/g, "U")
  .replace(/[^\x20-\x7E]/g, "").trim();
const _tlv = (id, val) => { const v = String(val); return `${id}${String(v.length).padStart(2, "0")}${v}`; };
const _crc16ccitt = (str) => {
  let crc = 0xFFFF;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) crc = (crc & 0x8000) ? (((crc << 1) ^ 0x1021) & 0xFFFF) : ((crc << 1) & 0xFFFF);
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
};
function buildFastQr({ iban, name, amount, ref }) {
  const clean = String(iban || "").replace(/\s/g, "").toUpperCase();
  if (clean.length < 16) return "";
  const amt = Number(amount) || 0;
  const merchant = _tlv("00", "TR.GOV.TCMB.FAST") + _tlv("01", clean);
  let s = "";
  s += _tlv("00", "01");
  s += _tlv("01", amt > 0 ? "12" : "11");
  s += _tlv("30", merchant);
  s += _tlv("53", "949");
  if (amt > 0) s += _tlv("54", amt.toFixed(2));
  s += _tlv("58", "TR");
  s += _tlv("59", _asciiTr(name).substring(0, 25) || "ALICI");
  if (ref) s += _tlv("62", _tlv("05", _asciiTr(ref).substring(0, 25)));
  s += "6304";
  return s + _crc16ccitt(s);
}

const dt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const INVOICE_A5_CSS = `
  #invoice-a5-root { color:#0A0A0A; font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; font-size:12px; background:#fff; --inv-primary:#064E3B; --inv-accent:#EA580C; --inv-primary-soft:#ECFDF5; --inv-primary-border:#d1fae5; --inv-zebra:#f6fdfa; --inv-accent-soft:#fff7ed; }
  #invoice-a5-root * { box-sizing:border-box; }
  #invoice-a5-root .inv-band { height:9px; background:var(--inv-primary); border-radius:5px 5px 0 0; overflow:hidden; }
  #invoice-a5-root .inv-band i { display:block; height:100%; width:38%; background:var(--inv-accent); }
  #invoice-a5-root .inv-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:14px 4px 12px; }
  #invoice-a5-root .inv-logo { height:52px; width:auto; display:block; margin-bottom:6px; }
  #invoice-a5-root .inv-brand { font-size:17px; font-weight:800; color:var(--inv-primary); letter-spacing:.3px; }
  #invoice-a5-root .inv-legal { font-weight:700; font-size:11px; color:#0A0A0A; }
  #invoice-a5-root .inv-sm { font-size:10.5px; color:#4B5563; line-height:1.55; }
  #invoice-a5-root .inv-titlewrap { text-align:right; min-width:190px; }
  #invoice-a5-root .inv-title { font-size:26px; font-weight:800; letter-spacing:1px; color:var(--inv-primary); line-height:1; }
  #invoice-a5-root .inv-metabox { margin-top:8px; background:var(--inv-primary-soft); border:1px solid var(--inv-primary-border); border-radius:8px; padding:8px 10px; font-size:10.5px; text-align:left; }
  #invoice-a5-root .inv-metabox .m { display:flex; justify-content:space-between; gap:18px; padding:2px 0; }
  #invoice-a5-root .inv-metabox .m b { color:var(--inv-primary); }
  #invoice-a5-root .inv-parties { display:flex; gap:12px; margin:6px 0 12px; align-items:stretch; }
  #invoice-a5-root .inv-party { flex:1; border:1px solid #e2e8f0; border-radius:8px; padding:9px 11px; }
  #invoice-a5-root .inv-party.cust { border-left:4px solid var(--inv-accent); background:var(--inv-accent-soft); }
  #invoice-a5-root .inv-party .lbl { font-size:9px; text-transform:uppercase; letter-spacing:1px; color:var(--inv-accent); font-weight:700; margin-bottom:3px; }
  #invoice-a5-root .inv-party .nm { font-weight:800; font-size:13px; color:#0A0A0A; }
  #invoice-a5-root .inv-qr { text-align:center; flex:0 0 auto; }
  #invoice-a5-root .inv-cap { font-size:9px; color:#4B5563; margin-top:3px; letter-spacing:.3px; }
  #invoice-a5-root .inv-table { width:100%; border-collapse:collapse; margin-top:2px; border-radius:8px; overflow:hidden; }
  #invoice-a5-root .inv-table th { background:var(--inv-primary); color:#fff; font-size:10.5px; text-align:left; padding:8px; font-weight:600; letter-spacing:.3px; }
  #invoice-a5-root .inv-table td { border-bottom:1px solid #eef2f7; padding:7px 8px; font-size:11px; }
  #invoice-a5-root .inv-table tbody tr:nth-child(even){ background:var(--inv-zebra); }
  #invoice-a5-root .inv-table .r, #invoice-a5-root .inv-table th.r { text-align:right; }
  #invoice-a5-root .inv-table .c, #invoice-a5-root .inv-table th.c { text-align:center; }
  #invoice-a5-root .inv-bottom { display:flex; gap:14px; margin-top:12px; align-items:flex-start; justify-content:flex-end; }
  #invoice-a5-root .inv-bankstrip { display:flex; gap:10px; align-items:center; margin-top:8px; border:1px solid var(--inv-primary-border); border-radius:6px; padding:4px 10px; background:var(--inv-primary-soft); }
  #invoice-a5-root .inv-bankcol { flex:1; font-size:9px; color:#334155; line-height:1.35; }
  #invoice-a5-root .inv-bankcol b { color:var(--inv-primary); }
  #invoice-a5-root .inv-bankcol > b:first-child { font-size:9.5px; }
  #invoice-a5-root .inv-bankline { padding:0; }
  #invoice-a5-root .inv-bankline .ib { color:var(--inv-primary); font-weight:700; }
  #invoice-a5-root .inv-fastqr { text-align:center; flex:0 0 auto; }
  #invoice-a5-root .inv-totals { width:46%; min-width:210px; }
  #invoice-a5-root .inv-trow { display:flex; justify-content:space-between; padding:4px 2px; font-size:11.5px; color:#334155; }
  #invoice-a5-root .inv-grand { margin-top:6px; background:var(--inv-primary); border-radius:8px; padding:10px 12px; display:flex; justify-content:space-between; align-items:center; }
  #invoice-a5-root .inv-grand .gl { color:#fff; font-weight:700; font-size:12px; letter-spacing:.5px; }
  #invoice-a5-root .inv-grand .gv { color:#fff; font-weight:800; font-size:19px; }
  #invoice-a5-root .inv-foot { display:flex; justify-content:space-between; align-items:flex-end; margin-top:16px; border-top:2px solid var(--inv-primary); padding-top:10px; gap:12px; }
  #invoice-a5-root .inv-sline { border-top:1px dotted #94a3b8; width:150px; height:34px; margin:0 auto 4px; }
  #invoice-a5-root .inv-thanks { font-size:11px; color:var(--inv-primary); font-weight:700; }
  /* ---- Belge Şablonları (klasik = varsayılan, kural yok) ---- */
  #invoice-a5-root[data-template="modern"] .inv-band { height:16px; }
  #invoice-a5-root[data-template="modern"] .inv-band i { width:55%; }
  #invoice-a5-root[data-template="modern"] .inv-title { font-size:30px; text-transform:uppercase; }
  #invoice-a5-root[data-template="modern"] .inv-table th { text-transform:uppercase; letter-spacing:.8px; }
  #invoice-a5-root[data-template="modern"] .inv-head { padding-top:18px; }
  #invoice-a5-root[data-template="minimal"] .inv-band { display:none; }
  #invoice-a5-root[data-template="minimal"] .inv-title { color:#0A0A0A; }
  #invoice-a5-root[data-template="minimal"] .inv-table th { background:#fff; color:#0A0A0A; border-bottom:2px solid #0A0A0A; }
  #invoice-a5-root[data-template="minimal"] .inv-table tbody tr:nth-child(even){ background:#fff; }
  #invoice-a5-root[data-template="minimal"] .inv-metabox { background:#fff; }
  #invoice-a5-root[data-template="minimal"] .inv-grand { background:#0A0A0A; }
  #invoice-a5-root[data-template="minimal"] .inv-bank { background:#fff; }
  #invoice-a5-root[data-template="zarif"] { font-family:'Georgia','Times New Roman',serif; }
  #invoice-a5-root[data-template="zarif"] .inv-band { height:5px; border-radius:0; }
  #invoice-a5-root[data-template="zarif"] .inv-title { font-weight:700; letter-spacing:3px; }
  #invoice-a5-root[data-template="zarif"] .inv-table { border:1px solid var(--inv-primary-border); }
  #invoice-a5-root[data-template="zarif"] .inv-table th { background:var(--inv-primary-soft); color:var(--inv-primary); border-bottom:2px solid var(--inv-primary); }
  #invoice-a5-root[data-template="zarif"] .inv-brand { letter-spacing:2px; }
  #invoice-a5-root[data-template="kompakt"] { font-size:10.5px; }
  #invoice-a5-root[data-template="kompakt"] .inv-head { padding:8px 4px 7px; }
  #invoice-a5-root[data-template="kompakt"] .inv-title { font-size:20px; }
  #invoice-a5-root[data-template="kompakt"] .inv-table th { padding:5px; font-size:9.5px; }
  #invoice-a5-root[data-template="kompakt"] .inv-table td { padding:4px 5px; font-size:10px; }
  #invoice-a5-root[data-template="kompakt"] .inv-parties { margin:4px 0 8px; }
  #invoice-a5-root[data-template="kompakt"] .inv-logo { height:40px; }
  #invoice-a5-root[data-template="kurumsal"] { border:2px solid var(--inv-primary); }
  #invoice-a5-root[data-template="kurumsal"] .inv-band { display:none; }
  #invoice-a5-root[data-template="kurumsal"] .inv-head { border-bottom:3px double var(--inv-primary); padding-top:14px; }
  #invoice-a5-root[data-template="kurumsal"] .inv-brand { text-transform:uppercase; letter-spacing:.6px; }
  #invoice-a5-root[data-template="kurumsal"] .inv-title { border:2px solid var(--inv-primary); padding:2px 10px; border-radius:6px; }
  #invoice-a5-root[data-template="premium"] .inv-band { height:20px; }
  #invoice-a5-root[data-template="premium"] .inv-band i { width:100%; background:var(--inv-accent); }
  #invoice-a5-root[data-template="premium"] .inv-title { letter-spacing:1.5px; }
  #invoice-a5-root[data-template="premium"] .inv-table th { background:var(--inv-primary); border-bottom:2px solid var(--inv-accent); }
  #invoice-a5-root[data-template="premium"] .inv-grand { border:1px solid var(--inv-accent); }
  #invoice-a5-root[data-template="ferah"] { font-size:12px; }
  #invoice-a5-root[data-template="ferah"] .inv-band { height:4px; }
  #invoice-a5-root[data-template="ferah"] .inv-head { padding-top:20px; padding-bottom:14px; }
  #invoice-a5-root[data-template="ferah"] .inv-table th { background:var(--inv-primary-soft); color:var(--inv-primary); }
  #invoice-a5-root[data-template="ferah"] .inv-table td { padding:9px 8px; }
  #invoice-a5-root[data-template="ferah"] .inv-grand { background:var(--inv-primary-soft); }
  #invoice-a5-root[data-template="ferah"] .inv-grand .gl, #invoice-a5-root[data-template="ferah"] .inv-grand .gv { color:var(--inv-primary); }
  #invoice-a5-root[data-template="kutu"] .inv-band { display:none; }
  #invoice-a5-root[data-template="kutu"] .inv-head { border:1.5px solid var(--inv-primary); border-radius:10px; padding:12px; }
  #invoice-a5-root[data-template="kutu"] .inv-table { border:1.5px solid var(--inv-primary); border-radius:10px; overflow:hidden; }
  #invoice-a5-root[data-template="kutu"] .inv-grand { border:1.5px solid var(--inv-primary); }
`;

// Satış faturasını izole yeni pencerede A4 yazdırır (80mm termal CSS ile çakışmaz).
export function printInvoiceNode() {
  const node = document.getElementById("invoice-a5-root");
  if (!node) return false;
  const w = window.open("", "_blank", "width=840,height=1120");
  if (!w) return false;
  w.document.write(`<html><head><title>Fatura</title><style>@page{size:A4;margin:14mm}body{margin:0;padding:0;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}${INVOICE_A5_CSS}</style></head><body>${node.outerHTML}<script>window.onload=function(){setTimeout(function(){window.print();setTimeout(function(){window.close()},400)},300)}<\/script></body></html>`);
  w.document.close();
  return true;
}

export function InvoiceA5Doc({ sale, docConfig }) {
  if (!sale) return null;
  const lines = sale.lines || [];
  const ref = sale.edoc?.doc_no || sale.sale_no || "";
  const cfg = docConfig || docCfg("invoice");
  const showBrand = cfg.show_brand && docShowBrandText();
  const title = cfg.title || (sale.edoc ? String(sale.edoc.doc_type_label || "FATURA").toUpperCase() : "SATIŞ FATURASI");
  const th = activeDocTheme();
  const themeVars = { "--inv-primary": th.primary, "--inv-accent": th.accent, "--inv-primary-soft": th.primarySoft, "--inv-primary-border": th.primaryBorder, "--inv-zebra": th.zebra, "--inv-accent-soft": th.accentSoft };
  const _banks = docBankAccounts();
  const _firstBank = _banks.find((b) => (b.iban || "").replace(/\s/g, "").length >= 16);
  const fastQrValue = _firstBank ? buildFastQr({ iban: _firstBank.iban, name: _firstBank.account_name || docLegalName(), amount: sale.total, ref }) : "";
  return (
    <div id="invoice-a5-root" data-testid="invoice-a5-root" data-template={activeDocTemplate()} style={docFontOverride() ? { ...themeVars, fontFamily: docFontOverride() } : themeVars}>
      <div className="inv-band"><i /></div>
      <div className="inv-head">
        <div>
          {cfg.show_logo && <img className="inv-logo" src={logoUrl()} alt={docBrandName()} />}
          {showBrand && <div className="inv-brand">{docBrandName()}</div>}
          <div className="inv-legal">{docLegalName()}</div>
          <div className="inv-sm">{docAddress()}</div>
          <div className="inv-sm">{docTaxLine()}</div>
          <div className="inv-sm">{docPhone()} · {docWeb()}</div>
          {cfg.header_lines.map((l, i) => <div key={i} className="inv-sm" data-testid="invoice-header-line">{l}</div>)}
        </div>
        {cfg.stamp && docStampPos() === "top" && (
          <div style={{ flex: "0 0 auto", alignSelf: "center", textAlign: "center", padding: "0 8px" }} data-testid="invoice-stamp-top">
            {docStampUrl()
              ? <img src={docImageUrl(docStampUrl())} alt="Kaşe & İmza" data-testid="invoice-stamp-img" style={{ display: "inline-block", maxHeight: "72px", maxWidth: "150px" }} />
              : <span data-testid="invoice-stamp-text" style={{ display: "inline-block", transform: "rotate(-5deg)", border: "2px solid #064E3B", color: "#064E3B", borderRadius: "8px", padding: "6px 16px", fontSize: "11px", fontWeight: 800, letterSpacing: "2px", lineHeight: 1.35, textAlign: "center" }}>RESMÎ BELGE<br /><span style={{ fontSize: "9px", fontWeight: 700 }}>{docBrandName()}</span></span>}
          </div>
        )}
        <div className="inv-titlewrap">
          <div className="inv-title">{title}</div>
          <div className="inv-metabox">
            <div className="m"><b>Belge No</b><span>{ref}</span></div>
            <div className="m"><b>Fiş No</b><span>{sale.sale_no}</span></div>
            <div className="m"><b>Tarih</b><span>{dt(sale.created_at)}</span></div>
          </div>
        </div>
      </div>
      <div className="inv-parties">
        <div className="inv-party cust">
          <div className="lbl">Sayın Müşteri</div>
          <div className="nm">{sale.customer_name || "Nihai Tüketici"}</div>
          {sale.customer_tax_no && <div className="inv-sm">{sale.customer_type === "individual" ? "T.C. Kimlik No" : "VKN"}: {sale.customer_tax_no}{sale.customer_tax_office ? ` · ${sale.customer_tax_office}` : ""}</div>}
          {sale.customer_address && <div className="inv-sm">{sale.customer_address}</div>}
          {sale.customer_phone && <div className="inv-sm">Tel: {sale.customer_phone}</div>}
          {sale.location_name && <div className="inv-sm">Satış Noktası: {sale.location_name}</div>}
        </div>
        {cfg.qr_verify && (
          <div className="inv-qr">
            <QRCodeSVG value={verifyUrl(ref)} size={66} level="M" />
            <div className="inv-cap">Belge Doğrula</div>
          </div>
        )}
      </div>
      <table className="inv-table">
        <thead>
          <tr><th className="c" style={{ width: "26px" }}>#</th><th>Ürün / Hizmet</th><th className="r">Miktar</th><th className="r">Birim Fiyat</th><th className="c">KDV</th><th className="r">Tutar</th></tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td>
              <td>{l.name}</td>
              <td className="r">{l.quantity}</td>
              <td className="r">{fmtTL(l.unit_price)}</td>
              <td className="c">{l.vat_rate != null ? `%${l.vat_rate}` : "-"}</td>
              <td className="r">{fmtTL(l.line_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="inv-bottom">
        <div className="inv-totals">
          <div className="inv-trow"><span>Ara Toplam (KDV hariç)</span><span>{fmtTL(sale.subtotal)}</span></div>
          <div className="inv-trow"><span>KDV</span><span>{fmtTL(sale.vat_total)}</span></div>
          {sale.discount > 0 && <div className="inv-trow"><span>İndirim</span><span>-{fmtTL(sale.discount)}</span></div>}
          {sale.meal_card_surcharge > 0 && <div className="inv-trow"><span>Yemek Kartı Kom.{sale.meal_card_provider ? ` (${sale.meal_card_provider})` : ""}</span><span>+{fmtTL(sale.meal_card_surcharge)}</span></div>}
          <div className="inv-grand"><span className="gl">GENEL TOPLAM</span><span className="gv" data-testid="invoice-total">{fmtTL(sale.total)}</span></div>
        </div>
      </div>
      <div className="inv-bankstrip" data-testid="invoice-bank">
        <div className="inv-bankcol">
          <b>Ödeme / Havale Bilgileri</b>
          {docBankAccounts().map((b, i) => (
            <div key={i} data-testid="invoice-bank-account" className="inv-bankline">
              {[b.bank_name, b.account_name].filter(Boolean).join(" · ")}{(b.bank_name || b.account_name) && b.iban ? " — " : ""}{b.iban ? <b className="ib">IBAN: {b.iban}</b> : null}
            </div>
          ))}
        </div>
        {fastQrValue && (
          <div className="inv-fastqr" data-testid="invoice-fast-qr">
            <QRCodeSVG value={fastQrValue} size={44} level="M" />
            <div className="inv-cap">FAST Karekod ile Öde</div>
          </div>
        )}
      </div>
      <div className="inv-foot">
        {cfg.qr_survey && (
          <div className="inv-qr">
            <QRCodeSVG value={surveyUrl(ref)} size={56} level="M" />
            <div className="inv-cap">Değerlendir / Anket</div>
          </div>
        )}
        <div style={{ textAlign: docStampPos() === "top" ? "center" : docStampPos() }}>
          <div className="inv-sline" />
          <div className="inv-sm">Kaşe / İmza</div>
          {cfg.stamp && docStampPos() !== "top" && (docStampUrl()
            ? <img src={docImageUrl(docStampUrl())} alt="Kaşe & İmza" data-testid="invoice-stamp-img" style={{ marginTop: "8px", display: "inline-block", maxHeight: "72px", maxWidth: "150px" }} />
            : <span data-testid="invoice-stamp-text" style={{ marginTop: "10px", display: "inline-block", transform: "rotate(-5deg)", border: "2px solid #064E3B", color: "#064E3B", borderRadius: "8px", padding: "6px 16px", fontSize: "11px", fontWeight: 800, letterSpacing: "2px", lineHeight: 1.35, textAlign: "center" }}>RESMÎ BELGE<br /><span style={{ fontSize: "9px", fontWeight: 700 }}>{docBrandName()}</span></span>)}
        </div>
        <div style={{ textAlign: "right" }}>
          {cfg.footer_lines.map((l, i) => i === 0
            ? <div key={i} className="inv-thanks" data-testid="invoice-footer-line">{l}</div>
            : <div key={i} className="inv-sm" data-testid="invoice-footer-line">{l}</div>)}
          {docSlogan() && <div className="inv-sm">{docSlogan()}</div>}
          <div className="inv-sm">{docEmail()}</div>
          {(docWebRaw() || showBrand) && <div className="inv-sm">{[docWebRaw(), showBrand ? docBrandName() : ""].filter(Boolean).join(" · ")}</div>}
        </div>
      </div>
    </div>
  );
}
